import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { GripVertical, Phone, Mail, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { id: "new_lead", label: "New Lead", color: "border-t-blue-400" },
  { id: "contacted", label: "Contacted", color: "border-t-yellow-400" },
  { id: "in_negotiation", label: "Negotiation", color: "border-t-orange-400" },
  { id: "will_call_when_needed", label: "Will Call When Needed", color: "border-t-purple-400" },
  { id: "closed_won", label: "Won", color: "border-t-green-500" },
  { id: "closed_lost", label: "Lost", color: "border-t-gray-400" },
];

const SOURCE_LABELS = { facebook: "FB", cold_call: "Call", referral: "Ref", website: "Web", manual: "Man" };

export default function CRMPipeline() {
  const [clients, setClients] = useState([]);
  const navigate = useNavigate();

  const fetchClients = async () => {
    try {
      const { data } = await axios.get(`${API}/crm/clients`, { withCredentials: true });
      setClients(data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchClients(); }, []);

  const moveClient = async (clientId, newStage) => {
    try {
      await axios.patch(`${API}/crm/clients/${clientId}/pipeline`, { pipeline_stage: newStage }, { withCredentials: true });
      setClients(prev => prev.map(c => c.id === clientId ? { ...c, pipeline_stage: newStage } : c));
    } catch (e) { console.error(e); }
  };

  const handleDragStart = (e, clientId) => {
    e.dataTransfer.setData("clientId", clientId);
  };

  const handleDrop = (e, stageId) => {
    e.preventDefault();
    const clientId = e.dataTransfer.getData("clientId");
    if (clientId) moveClient(clientId, stageId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  return (
    <div data-testid="crm-pipeline" className="p-8">
      <h1 className="font-heading text-2xl font-bold text-gray-900 mb-6">Sales Pipeline</h1>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 pb-4">
        {STAGES.map((stage) => {
          const stageClients = clients.filter(c => c.pipeline_stage === stage.id);
          return (
            <div
              key={stage.id}
              data-testid={`pipeline-column-${stage.id}`}
              className={`min-w-0 bg-gray-50 border border-gray-200 rounded-sm border-t-4 ${stage.color}`}
              onDrop={(e) => handleDrop(e, stage.id)}
              onDragOver={handleDragOver}
            >
              <div className="p-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-sm font-bold text-gray-900">{stage.label}</h3>
                  <span className="text-xs font-mono bg-white border border-gray-200 rounded-full px-2 py-0.5">{stageClients.length}</span>
                </div>
              </div>
              <div className="p-2 space-y-2 min-h-[300px]">
                {stageClients.map((client) => (
                  <div
                    key={client.id}
                    data-testid={`pipeline-card-${client.id}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, client.id)}
                    onClick={() => navigate(`/admin/crm/clients/${client.id}`)}
                    title="Click to open client profile"
                    className="bg-white border border-gray-200 rounded-sm p-3 cursor-pointer hover:border-yellow-400 hover:shadow-sm transition-all group"
                  >
                    <div className="flex items-start gap-2">
                      <GripVertical className="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5 cursor-grab active:cursor-grabbing" onClick={(e) => e.stopPropagation()} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1">
                          <p className="text-sm font-medium text-gray-900 truncate group-hover:text-yellow-700">{client.name}</p>
                          <ExternalLink className="w-3 h-3 text-gray-300 group-hover:text-yellow-600 flex-shrink-0" />
                        </div>
                        {client.company && <p className="text-xs text-gray-500 truncate">{client.company}</p>}
                        <div className="flex items-center gap-2 mt-1.5">
                          {client.phone && (
                            <a href={`tel:${client.phone}`} onClick={(e) => e.stopPropagation()} className="text-xs text-gray-400 hover:text-gray-700 flex items-center gap-0.5">
                              <Phone className="w-3 h-3" />
                            </a>
                          )}
                          {client.email && (
                            <a href={`mailto:${client.email}`} onClick={(e) => e.stopPropagation()} className="text-xs text-gray-400 hover:text-gray-700 flex items-center gap-0.5">
                              <Mail className="w-3 h-3" />
                            </a>
                          )}
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">{SOURCE_LABELS[client.source] || client.source}</Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-gray-400 mt-3">Click a card to open the client profile · Drag &amp; drop between stages to update pipeline status.</p>
    </div>
  );
}
